/**
 * @fileoverview ReportTransactionList - Simplified read-only transaction list for reports.
 *
 * @description Displays transactions in a read-only format with:
 * - Date, Description, Amount, Categories
 * - Sortable by date (newest/oldest first)
 * - No edit/delete functionality (reports are read-only)
 * - Optional category highlighting
 */
import { useState } from 'react';
import { formatDateForDisplay } from '../../utils/dateUtils';
import { formatCurrency } from '../../utils/moneyUtils';
import styles from './ReportTransactionList.module.css';

/**
 * Report Transaction List Component
 *
 * @param {Object} props - Component props
 * @param {Array} props.transactions - List of transactions to display
 * @param {number} props.highlightCategoryKey - Optional category key to highlight
 */
function ReportTransactionList({ transactions, highlightCategoryKey }) {
  const [sortOrder, setSortOrder] = useState('desc'); // 'desc' = newest first

  if (transactions.length === 0) {
    return (
      <div className={styles.emptyMessage}>
        No transactions found for the selected criteria
      </div>
    );
  }

  // Sort transactions by date, then by createdAt (fdtmwhen)
  const sortedTransactions = [...transactions].sort((a, b) => {
    const dateA = new Date(a.date);
    const dateB = new Date(b.date);
    const createdAtA = new Date(a.createdAt);
    const createdAtB = new Date(b.createdAt);

    if (sortOrder === 'desc') {
      // Newest first: sort by date DESC, then createdAt DESC
      if (dateB.getTime() !== dateA.getTime()) {
        return dateB - dateA;
      }
      return createdAtB - createdAtA;
    } else {
      // Oldest first: sort by date ASC, then createdAt ASC
      if (dateA.getTime() !== dateB.getTime()) {
        return dateA - dateB;
      }
      return createdAtA - createdAtB;
    }
  });

  const toggleSort = () => {
    setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <span className={styles.count}>({transactions.length} transaction{transactions.length !== 1 ? 's' : ''})</span>
        <button
          onClick={toggleSort}
          className={styles.sortButton}
          title={sortOrder === 'desc' ? 'Click to show oldest first' : 'Click to show newest first'}
        >
          {sortOrder === 'desc' ? '↓ Newest First' : '↑ Oldest First'}
        </button>
      </div>

      <div className={styles.transactionsList}>
        {sortedTransactions.map((transaction) => (
          <TransactionItem
            key={transaction.id}
            transaction={transaction}
            highlightCategoryKey={highlightCategoryKey}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * Individual Transaction Item Component
 */
function TransactionItem({ transaction, highlightCategoryKey }) {
  const [isExpanded, setIsExpanded] = useState(false);

  // Determine type badge class
  const typeBadgeClass = `${styles.typeBadge} ${styles[transaction.type.toLowerCase()]}`;

  return (
    <div className={styles.transactionItem}>
      <div className={styles.mainRow} onClick={() => setIsExpanded(!isExpanded)}>
        <span className={styles.date}>{formatDateForDisplay(transaction.date)}</span>
        <span className={typeBadgeClass}>{transaction.type}</span>
        <span className={styles.amount}>{formatCurrency(Math.abs(transaction.amount))}</span>
        {transaction.paidBy && (
          <span className={styles.paidByBadge} title={transaction.paidBy}>
            {transaction.paidBy.substring(0, 6)}
          </span>
        )}
        <span className={styles.description}>{transaction.description}</span>
        <button className={styles.expandButton} aria-label={isExpanded ? 'Collapse' : 'Expand'}>
          {isExpanded ? '▼' : '▶'}
        </button>
      </div>

      {isExpanded && (
        <div className={styles.details}>
          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>Categories:</span>
            <span className={styles.detailValue}>
              {transaction.categories.map((cat) => {
                const isHighlighted = highlightCategoryKey && cat.categoryKey === highlightCategoryKey;
                return (
                  <span
                    key={cat.categoryKey}
                    className={isHighlighted ? styles.highlightedCategory : styles.category}
                  >
                    {cat.category}: {cat.amount >= 0 ? '+' : ''}{formatCurrency(Math.abs(cat.amount))}
                  </span>
                );
              })}
            </span>
          </div>
          {transaction.paidBy && (
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>Paid by:</span>
              <span className={styles.detailValue}>{transaction.paidBy}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ReportTransactionList;
